import { CoreResponseStatus } from 'cw-isdk/core';
import { ServiceTypes } from 'cw-isdk/core/service-types';
import { concatMap, Observable } from 'rxjs';
import { Inject, Service } from 'typedi';
import { CSRFUtil } from '../csrf-util';
import { UrlUtil } from '../url-util';
import { CoreResponseError } from './core-response-error';
import { HttpService } from './http.service';
import { API_BEARER_TOKEN_CONFIG, ApiBearerTokenConfig } from './api-bearer-token-config';

@Service()
export class ApiService {

    private readonly _apiBearerTokenConfig: ApiBearerTokenConfig;
    private readonly _httpService: HttpService;

    public constructor(
        httpService: HttpService,
        @Inject(API_BEARER_TOKEN_CONFIG) apiBearerTokenConfig: ApiBearerTokenConfig,
    ) {
        this._httpService = httpService;
        this._apiBearerTokenConfig = apiBearerTokenConfig;
    }

    public download<TData>(options: ApiRequestOptions<TData>): void {
        const url = options.baseUrl == null
            ? UrlUtil.makeStandardApiUrl(options.area, options.service, options.action).toString()
            : UrlUtil.makeStandardApiUrl(options.baseUrl, options.area, options.service, options.action).toString();

        return this._httpService.download(url, options.data ?? {});
    }

    public get<TData>(responseType: 'httpResponse', options: ApiRequestOptions<TData>): Observable<Response>;
    public get<TData, TResponse>(responseType: 'coreResponse', options: ApiRequestOptions<TData>): Observable<ServiceTypes.CoreResponseBase_<TResponse>>;
    public get<TData, TResponse>(responseType: 'coreResponseValue', options: ApiRequestOptions<TData>): Observable<TResponse>;
    public get<TData, TResponse>(responseType: ApiResponseType, options: ApiRequestOptions<TData>): Observable<TResponse> {

        const url = options.baseUrl == null
            ? UrlUtil.makeStandardApiUrl(options.area, options.service, options.action).toString()
            : UrlUtil.makeStandardApiUrl(options.baseUrl, options.area, options.service, options.action).toString();

        return this._httpService.get(url, 'response', {
            headers: this.#buildRequestHeaders(new URL(url)),
            query: new URLSearchParams({ data: JSON.stringify(options.data ?? {}) }),
        }).pipe(
            concatMap(response =>
                this.#processResponse<TResponse>(response, responseType)
            )
        );
    }

    public post<TData>(responseType: 'httpResponse', options: ApiRequestOptions<TData>): Observable<Response>;
    public post<TData, TResponse>(responseType: 'coreResponse', options: ApiRequestOptions<TData>): Observable<ServiceTypes.CoreResponseBase_<TResponse>>;
    public post<TData, TResponse>(responseType: 'coreResponseValue', options: ApiRequestOptions<TData>): Observable<TResponse>;
    public post<TData, TResponse>(responseType: ApiResponseType, options: ApiRequestOptions<TData>): Observable<TResponse> {

        const data = JSON.stringify(options.data ?? {});
        const url = UrlUtil.makeStandardApiUrl(options.area, options.service, options.action).toString();
        const headers = this.#buildRequestHeaders(
            new URL(url),
            options.contentType === 'json' ? { 'Content-Type': 'application/json' } : {}
        );

        let postObservable: Observable<Response>;
        if (options.contentType === 'json') {
            postObservable = this._httpService.post(url, {
                body: data,
                headers: headers,
            });
        } else {
            postObservable = this._httpService.postUrlEncodedBody(url, {
                body: new URLSearchParams({ data }),
                headers: headers,
            });
        }

        return postObservable.pipe(
            concatMap(response =>
                this.#processResponse<TResponse>(response, responseType)
            )
        );
    }

    #buildRequestHeaders(
        url: URL,
        extraHeaders?: Record<string, string>,
    ): Record<string, string> {

        const headers: Record<string, string> = {
            ...(extraHeaders ?? {}),
            ...(CSRFUtil.getCSRFHeader() ?? {}),
        };

        const bearerToken = this._apiBearerTokenConfig.bearerTokenProvider?.(url);
        if (bearerToken) {
            headers['Authorization'] = `bearer ${bearerToken}`;
        }

        return headers;
    }

    #processResponse<TResponse>(response: Response, responseType: ApiResponseType): Observable<TResponse> {

        return new Observable<TResponse>(subscriber => {

            if (responseType === 'httpResponse') {
                subscriber.next(response as unknown as TResponse);
                subscriber.complete();
                return;
            }

            response.json().then(
                (coreResponse: CoreResponse) => {

                    if (response.status !== 200 || coreResponse.Status !== 0 || (coreResponse.ErrorMessages ?? []).length > 0) {
                        subscriber.error(new CoreResponseError(coreResponse, undefined, response));
                    } else if (responseType === 'coreResponse') {
                        subscriber.next(coreResponse as unknown as TResponse);
                    } else {
                        subscriber.next(coreResponse.Value as TResponse);
                    }
                    subscriber.complete();
                },
                error => {
                    if (error instanceof DOMException && error.code === DOMException.ABORT_ERR) {
                        subscriber.complete();
                    } else {
                        subscriber.error(new CoreResponseError(undefined, error, response));
                    }
                }
            );
        });
    }
}

export interface CoreResponse {

    ErrorMessages: CoreResponseMessage[];
    Status: CoreResponseStatus;
    SuccessMessages: CoreResponseMessage[];
    Value: unknown;
    WarningMessages: CoreResponseMessage[];

}

export interface CoreResponseMessage {
    Code: number;
    DebugDetails: string;
    DisplayText: string;
    MessageType: number;
    Name: string;
    Service: string;
}

export interface ApiRequestOptions<TData> {
    action: string;
    area: string;
    baseUrl?: URL,
    /**
     * Set to json to send the request
     * object as JSON in the request body with
     * content-type header set to application/json
     */
    contentType?: 'json';
    data?: TData;
    service: string;
    query?: URLSearchParams;
}

/**
 * httpResponse:
 *   Return unchanged Response. Does not throw.
 * coreResponse:
 *   Return the CoreResponse object. Throws if:
 *     1. response status code is not 200
 *     2. response is not CoreResponse
 *     3. CoreResponse.Status != 0
 *     4. CoreResponse.ErrorMessages.length >0
 * value:
 *   Return the CoreResponse.Value object (default) throws
 *   for same reasons as 'coreResponse'
 */
export type ApiResponseType = 'httpResponse' | 'coreResponse' | 'coreResponseValue';
